import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth0 } from "@auth0/auth0-react";

function addMinutesToTime(timeStr, minutesToAdd) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours);
    startDate.setMinutes(minutes);
    startDate.setSeconds(0);
    startDate.setMilliseconds(0);

    const endDate = new Date(startDate.getTime() + minutesToAdd * 60000);

    const endHours = endDate.getHours().toString().padStart(2, '0');
    const endMinutes = endDate.getMinutes().toString().padStart(2, '0');

    return `${endHours}:${endMinutes}`;
}


const AppointmentModal = ({ showModal, setShowModal }) => {
    const { user } = useAuth0();

    const [formData, setFormData] = useState({
        nailService: '',
        length: '',
        design: '',
        soakOff: '',
        date: '',
        startTime: '',
        endTime: '',
        nailTech: '',
    });

    const selectedService = formData.nailService;
    const isServiceSelected = selectedService && selectedService !== 'Select';

    const isLengthDisabled = !isServiceSelected ||
        selectedService === 'Gel Polish Mani' ||
        selectedService === 'Hard Gel Overlay' ||
        selectedService === 'Free-Style Set';

    const isDesignDisabled = !isServiceSelected ||
        selectedService === 'Free-Style Set';

    const isSoakOffDisabled = !isServiceSelected;

    const shouldPrePopulateLength =
        selectedService === 'Gel Polish Mani' ||
        selectedService === 'Hard Gel Overlay' ||
        selectedService === 'Free-Style Set';

    const shouldPrePopulateDesign =
        selectedService === 'Free-Style Set';

    const [instagram, setInstagram] = useState('');
    const [customer, setCustomer] = useState(null);
    const [customerCompleted, setCustomerCompleted] = useState(false);

    useEffect(() => {
        const updated = { ...formData };

        // Reset disabled fields to 'Select'
        if (!isServiceSelected) {
            updated.length = '';
            updated.design = '';
            updated.soakOff = '';
        }

        // Pre-population rules
        if (selectedService === 'Gel Polish Mani' || selectedService === 'Hard Gel Overlay') {
            updated.length = 'Any';
            if (updated.design === 'Any') updated.design = '';
        } else if (selectedService === 'Free-Style Set') {
            updated.length = 'Any';
            updated.design = 'Any';
        } else if (selectedService === 'Gel-X Extension') {
            if (updated.length === 'Any') updated.length = '';
            if (updated.design === 'Any') updated.design = '';
        } else {
            // Catch all for any other case: remove "Any" if it exists
            if (updated.length === 'Any') updated.length = '';
            if (updated.design === 'Any') updated.design = '';
        }

        // Reset values of disabled fields to empty (deselect them)
        if (isLengthDisabled && updated.length !== 'Any') {
            updated.length = '';
        }

        if (isDesignDisabled && updated.design !== 'Any') {
            updated.design = '';
        }

        if (isSoakOffDisabled) {
            updated.soakOff = '';
        }

        setFormData(updated);
    }, [selectedService, isLengthDisabled, isDesignDisabled, isSoakOffDisabled]);

    useEffect(() => {
        if (!formData.startTime) return;

        let totalDuration = 0;

        const services = {
            'Gel Polish Mani': 60,
            'Hard Gel Overlay': 90,
            'Gel-X Extension': 60,
            'Free-Style Set': 60,
        };

        const lengths = {
            'Gel-X Extensions - Short': 0,
            'Gel-X Extensions - Medium': 0,
            'Gel-X Extensions - Long': 0,
            'Gel-X Extensions - Extra Long': 0,
        };

        const designs = {
            'French Tip Only': 0,
            'Tier 1 - Simple': 0,
            'Tier 2 - Intermediate': 0,
            'Tier 3 - Advanced': 30,
            'Tier 4 - Sophisticated': 30,
            'No Design': 0,
        };

        const soakOffs = {
            'Gel Polish Soak-Off': 0,
            'Gel-X Soak-Off': 30,
            'Acrylic Soak-Off': 30,
            'No Soak-Off': 0
        };

        if (services[formData.nailService]) totalDuration += services[formData.nailService];
        if (lengths[formData.length]) totalDuration += lengths[formData.length];
        if (designs[formData.design]) totalDuration += designs[formData.design];
        if (soakOffs[formData.soakOff]) totalDuration += soakOffs[formData.soakOff];

        const newEndTime = addMinutesToTime(formData.startTime, totalDuration);
        console.log("✅ New end time calculated:", newEndTime);

        setFormData(prev => ({
            ...prev,
            endTime: newEndTime,
        }));
    }, [formData.startTime, formData.nailService, formData.length, formData.design, formData.soakOff]);

    const closeModal = () => { setShowModal(false); setCustomerCompleted(false) }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };


    const handleCustomerChange = (e) => {
        const { name, value } = e.target;
        const updatedCustomer = {
            ...customer,
            [name]: value,
        };

        // Set instagram state if updated
        if (name === "instagram") {
            setInstagram(value);
        }

        // Add a 'name' key combining first and last name
        const firstName = name === 'firstName' ? value : customer?.firstName || '';
        const lastName = name === 'lastName' ? value : customer?.lastName || '';
        updatedCustomer.name = `${firstName} ${lastName}`.trim();

        setCustomer(updatedCustomer);
    };

    const isCustomerInfoValid = () => {
        if (!customer) return false;
        const { firstName, lastName, email, instagram } = customer;

        // Basic email format check using regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isEmailValid = emailRegex.test(email?.trim());

        return (
            firstName?.trim() &&
            lastName?.trim() &&
            instagram?.trim() &&
            isEmailValid
        );
    };

    const handleSubmit = async () => {
        try {
            const {
                nailService,
                length,
                design,
                soakOff,
                date,
                startTime,
                endTime,
                nailTech,
            } = formData;

            if (!nailService || !length || !design || !soakOff || !date || !startTime || !endTime || !nailTech) {
                alert("Please input all fields.");
                return;
            }

            const selectedDateTime = new Date(`${date}T${startTime}`);
            const now = new Date();

            if (selectedDateTime < now) {
                alert("Please select a future time.");
                return;
            }

            const appointmentSlot = {
                artist: nailTech,
                date: new Date(`${date}T${startTime}`),
                duration: 60,
                isBooked: false,
            };

            const appointmentDetails = [
                nailService,
                length,
                design,
                soakOff,
                'No preference',
                appointmentSlot,
            ];

            const answers = [
                { label: nailService, skips: 1, time: 60, price: 28 },
                null,
                { label: design, skips: 0, time: 30, price: 20 },
                { label: soakOff, skips: 0, time: 30, price: 10 },
            ];

            const localDateTime = formatDate(appointmentSlot.date);

            // 🔍 Check for overlaps before proceeding
            const overlapRes = await axios.post(
                'https://bunny-klawz.uc.r.appspot.com/appointments/check-overlap',
                { appointmentDetails }
            );

            if (overlapRes.data.overlap) {
                const confirmContinue = window.confirm(
                    "⚠️ Warning: This time slot is already booked for the selected nail tech. Do you want to proceed anyway?"
                );
                if (!confirmContinue) {
                    return; // Stop submission
                }
            }

            // ✅ Continue with appointment creation
            const payload = {
                user: customer,
                instagram,
                appointmentDetails,
                answers,
                localDateTime,
                paymentCompleted: true,
                status: 'pending',
                adminCreated: true,
            };

            const response = await axios.post(
                'https://bunny-klawz.uc.r.appspot.com/appointments',
                payload
            );

            console.log('✅ Appointment created:', response.data);

            // Send Stripe link email
            const stripeRes = await axios.post(
                'https://bunny-klawz.uc.r.appspot.com/send-stripe-link',
                {
                    toEmail: customer.email,
                    toName: customer.name,
                    appointmentDetails,
                    appointmentId: response.data._id
                }
            );

            console.log('📩 Stripe link email sent:', stripeRes.data);
            alert(`Appointment request with deposit link has been sent to ${customer.email} for ${formatDate(appointmentDetails[5].date)}. Client must pay a deposit within the next 12 hours to have the request updated to "Pending" status.`);
            closeModal();
        } catch (err) {
            console.error('❌ Error submitting appointment:', err);
        }
    };

    function formatDate(timestamp) {
        const date = new Date(timestamp);

        // Formatting components
        const month = date.toLocaleString('en-US', { month: 'short' }); // "Feb"
        const day = date.getDate(); // 28
        const year = date.getFullYear(); // 2025
        const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase(); // "8:00am"

        return `${month} ${day}, ${year} at ${time}`;
    }

    return (
        <>
            {showModal && (
                <div className="modal-overlay appt">
                    <div className="modal AppointmentCreationAdminModal">
                        <h2>Schedule Appointment</h2>

                        {/* <label>
                            Instagram Handle:
                            <input
                                type="text"
                                value={instagram}
                                onChange={(e) => setInstagram(e.target.value)}
                            />
                        </label> */}

                        {customerCompleted ? (<>
                            <p className='whitePriviledge'>Please select client’s requested services.</p>
                            <div className="OptionSet1">
                                <label>
                                    Nail Service:
                                    <select className="AppointmentCreationAdminInputsP2" name="nailService" onChange={handleChange}>
                                        <option>Select</option>
                                        <option>Gel Polish Mani</option>
                                        <option>Hard Gel Overlay</option>
                                        <option>Gel-X Extension</option>
                                        <option>Free-Style Set</option>
                                    </select>
                                </label>
                                <label>
                                    Length:
                                    <select
                                        className="AppointmentCreationAdminInputsP2"
                                        name="length"
                                        onChange={handleChange}
                                        disabled={isLengthDisabled}
                                        value={formData.length}
                                    >
                                        <option>Select</option>
                                        <option>Gel-X Extensions - Short</option>
                                        <option>Gel-X Extensions - Medium</option>
                                        <option>Gel-X Extensions - Long</option>
                                        <option>Gel-X Extensions - Extra Long</option>
                                        {(selectedService === 'Gel Polish Mani' ||
                                            selectedService === 'Hard Gel Overlay' ||
                                            selectedService === 'Free-Style Set') && <option>Any</option>}
                                    </select>
                                </label>

                                <label>
                                    Design:
                                    <select
                                        className="AppointmentCreationAdminInputsP2"
                                        name="design"
                                        onChange={handleChange}
                                        disabled={isDesignDisabled}
                                        value={formData.design}
                                    >
                                        <option>Select</option>
                                        <option>French Tip Only</option>
                                        <option>Tier 1 - Simple</option>
                                        <option>Tier 2 - Intermediate</option>
                                        <option>Tier 3 - Advanced</option>
                                        <option>Tier 4 - Sophisticated</option>
                                        <option>No Design</option>
                                        {selectedService === 'Free-Style Set' && <option>Any</option>}
                                    </select>
                                </label>

                                <label>
                                    Soak-Off:
                                    <select
                                        className="AppointmentCreationAdminInputsP2"
                                        name="soakOff"
                                        onChange={handleChange}
                                        disabled={isSoakOffDisabled}
                                        value={formData.soakOff}
                                    >
                                        <option>Select</option>
                                        <option>Gel Polish Soak-Off</option>
                                        <option>Gel-X Soak-Off</option>
                                        <option>Acrylic Soak-Off</option>
                                        <option>No Soak-Off</option>
                                    </select>
                                </label>
                            </div>

                            <p className='whitePriviledge'>Add client’s preferred appointment date and time.</p>
                            <div className="OptionSet2">
                                <label>
                                    Appointment Date:
                                    <input
                                        className="AppointmentCreationAdminInputsP2"
                                        type="date"
                                        name="date"
                                        onChange={handleChange}
                                        min={new Date().toISOString().split("T")[0]}
                                    />
                                </label>

                                <label>
                                    Preferred Time:
                                    <input
                                        className="AppointmentCreationAdminInputsP2"
                                        type="time"
                                        name="startTime"
                                        onChange={handleChange}
                                        min={formData.date === new Date().toISOString().split("T")[0]
                                            ? new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                                            : undefined}
                                    />
                                </label>

                                <label>
                                    Est. End Time:
                                    <input className="AppointmentCreationAdminInputsP2" type="time" name="endTime" value={formData.endTime} disabled readOnly />
                                </label>

                                <label>
                                    Nail Tech:
                                    <select className="AppointmentCreationAdminInputsP2" name="nailTech" onChange={handleChange}>
                                        <option>Select</option>
                                        <option>Leah</option>
                                        <option>Kayla</option>
                                        <option>Kelly</option>
                                    </select>
                                </label>
                            </div>
                        </>) :
                            (<>
                                <label className='UserInfoHeader'>Please add client’s information.</label>
                                <div className='UserInfo'>
                                    <label className='customerLabels'>First Name: <input className="customerInputs" name="firstName" placeholder='Enter' onChange={handleCustomerChange}></input></label>
                                    <label className='customerLabels'>Last Name: <input className="customerInputs" name="lastName" placeholder='Enter' onChange={handleCustomerChange}></input></label>
                                    <label className='customerLabels'>Insta:
                                        <input
                                            className="customerInputs"
                                            name="instagram"
                                            placeholder='Enter'
                                            value={instagram}
                                            onChange={(event) => {
                                                let value = event.target.value;

                                                // Remove all spaces
                                                value = value.replaceAll(" ", "");

                                                // Ensure it starts with '@'
                                                if (!value.startsWith("@")) {
                                                    value = "@" + value.replace(/^@+/, ""); // avoid multiple '@'
                                                }

                                                // Limit to 33 characters
                                                if (value.length > 33) {
                                                    value = value.slice(0, 33);
                                                }
                                                // Create a synthetic event to pass to your handler
                                                const syntheticEvent = {
                                                    target: {
                                                        name: "instagram",
                                                        value: value
                                                    }
                                                };

                                                handleCustomerChange(syntheticEvent);
                                                setInstagram(value); // this keeps input in sync
                                            }}></input>
                                    </label>
                                    <label className='customerLabels'>Email: <input className="customerInputs" name="email" placeholder='Enter' onChange={handleCustomerChange}></input></label>
                                </div>

                            </>)
                        }

                        <div className="modal-buttons">
                            <button onClick={closeModal} className="modal-close">
                                <img
                                    className="close-button-icon"
                                    src="/images/CloseButton.png"
                                    alt="Close Button Icon"
                                />
                            </button>

                            {
                                customerCompleted ? (
                                    <>
                                        <button onClick={() => { setCustomerCompleted(false) }} className="confirm customerCompletedGoBack AppointmentCreationButton">
                                            Go Back
                                        </button>
                                        <button onClick={handleSubmit} className="confirm AppointmentCreationButton">
                                            Confirm
                                        </button>
                                    </>
                                ) :
                                    (
                                        <button
                                            onClick={() => {
                                                if (!customer) {
                                                    alert("Please complete customer information.");
                                                    return;
                                                }

                                                const { firstName, lastName, email, instagram } = customer;

                                                if (!firstName?.trim() || !lastName?.trim() || !instagram?.trim() || !email?.trim()) {
                                                    alert("All fields are required.");
                                                    return;
                                                }

                                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                                if (!emailRegex.test(email.trim())) {
                                                    alert("Please enter a valid email address.");
                                                    return;
                                                }

                                                setCustomerCompleted(true);
                                            }}
                                            className="confirm"
                                        >
                                            Continue
                                        </button>
                                    )
                            }
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AppointmentModal;
